(() => {
  const money = value => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format((Number(value) || 0) / 100);
    } catch (_) {
      return `₹${Math.round((Number(value) || 0) / 100)}`;
    }
  };

  const escapeHTML = value => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  async function getCart() {
    try {
      const r = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) return { items: [] };
      return await r.json();
    } catch (_) {
      return { items: [] };
    }
  }

  function profileSignals() {
    try {
      if (window.BowkartAIProfile && window.BowkartAIProfile.get) {
        return window.BowkartAIProfile.get() || {};
      }
      return JSON.parse(localStorage.getItem('bk_ai_personalization_v2')) || {};
    } catch (_) {
      return {};
    }
  }

  function cartForAPI(cart) {
    return (cart.items || []).map(item => ({
      product_id: item.product_id,
      variant_id: item.variant_id,
      handle: item.handle,
      title: item.product_title || item.title,
      variant_title: item.variant_title || '',
      product_type: item.product_type || '',
      quantity: item.quantity,
      price: item.final_price || item.price
    }));
  }

  async function hydrate(handle) {
    try {
      const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) return null;

      const p = await r.json();
      const available = (p.variants || []).filter(v => v.available);
      if (!available.length) return null;

      const cheapest = available.reduce((a, b) =>
        Number(a.price) <= Number(b.price) ? a : b
      );

      const image = p.featured_image || (p.images && p.images[0]) || '';

      return {
        handle: p.handle,
        title: p.title,
        url: p.url || `/products/${p.handle}`,
        image,
        price: cheapest.price,
        compare_at_price: cheapest.compare_at_price || 0,
        variant_id: cheapest.id
      };
    } catch (_) {
      return null;
    }
  }

  function productCard(product, row, position) {
    const compare = Number(product.compare_at_price || 0);
    const price = Number(product.price || 0);
    const sale = compare > price;

    return `
      <article
        class="bk-ai-product"
        data-bk-ai-product
        data-handle="${escapeHTML(product.handle)}"
        data-section-key="${escapeHTML(row.key)}"
        data-section-title="${escapeHTML(row.title)}"
        data-position="${position}"
      >
        <a
          class="bk-ai-product__image-wrap"
          href="${escapeHTML(product.url)}"
          data-bk-ai-product-link
        >
          ${product.image
            ? `<img class="bk-ai-product__image" src="${escapeHTML(product.image)}" alt="${escapeHTML(product.title)}" loading="lazy" width="520" height="650">`
            : '<div class="bk-ai-product__placeholder"></div>'}
          ${sale ? '<span class="bk-ai-product__badge">Special</span>' : ''}
        </a>

        <div class="bk-ai-product__body">
          <a
            class="bk-ai-product__title"
            href="${escapeHTML(product.url)}"
            data-bk-ai-product-link
          >${escapeHTML(product.title)}</a>

          <div class="bk-ai-product__price">
            <span>${money(price)}</span>
            ${sale ? `<s>${money(compare)}</s>` : ''}
          </div>

          <button
            class="bk-ai-product__add"
            type="button"
            data-bk-ai-add="${escapeHTML(product.variant_id)}"
          >
            Quick add
          </button>
        </div>
      </article>`;
  }

  function analyticsContext(section) {
    return section.__bkAiAnalytics || {
      eventEndpoint: '',
      profile: {},
      sessionId: ''
    };
  }

  function sendEvent(section, eventName, card) {
    const ctx = analyticsContext(section);
    if (!ctx.eventEndpoint) return;

    const payload = {
      event: eventName,
      session_id: ctx.sessionId || '',
      section_key: card ? card.dataset.sectionKey || '' : '',
      section_title: card ? card.dataset.sectionTitle || '' : '',
      handle: card ? card.dataset.handle || '' : '',
      position: card ? Number(card.dataset.position || 0) : 0,
      visitor_type: ctx.profile.visitor_type || '',
      likely_age: ctx.profile.likely_age || '',
      preferred_category: ctx.profile.preferred_category || ''
    };

    try {
      fetch(ctx.eventEndpoint, {
        method: 'POST',
        mode: 'cors',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }).catch(() => {});
    } catch (_) {}
  }

  async function renderSection(row, host, parentSection) {
    const handles = (row.handles || []).filter(Boolean);
    if (!handles.length) return;

    const hydrated = (await Promise.all(handles.map(hydrate))).filter(Boolean);
    if (!hydrated.length) return;

    const el = document.createElement('section');
    el.className = `bk-ai-row bk-ai-row--${escapeHTML(row.key)}`;
    el.dataset.bkAiRow = row.key;
    el.dataset.bkAiRowTitle = row.title;

    el.innerHTML = `
      <div class="bk-ai-row__head">
        <div>
          <span class="bk-ai-row__eyebrow">FOR YOU</span>
          <h2>${escapeHTML(row.title)}</h2>
        </div>
      </div>

      <div class="bk-ai-row__products">
        ${hydrated.map((product, index) => productCard(product, row, index + 1)).join('')}
      </div>
    `;

    host.appendChild(el);

    const firstCard = el.querySelector('[data-bk-ai-product]');
    if (firstCard) sendEvent(parentSection, 'section_impression', firstCard);
  }

  async function quickAdd(button, parentSection) {
    const variantId = Number(button.dataset.bkAiAdd);
    if (!variantId) return;

    const card = button.closest('[data-bk-ai-product]');
    const original = button.textContent;

    sendEvent(parentSection, 'quick_add', card);

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!r.ok) throw new Error('Add failed');

      button.textContent = 'Added ✓';
      sendEvent(parentSection, 'quick_add_success', card);

      document.dispatchEvent(new CustomEvent('bowkart:cart:changed'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 1600);
    } catch (_) {
      button.textContent = 'Try again';
      button.disabled = false;
      sendEvent(parentSection, 'quick_add_error', card);
    }
  }

  async function init(section) {
    if (section.dataset.bkAiReady === '1') return;
    section.dataset.bkAiReady = '1';

    const endpoint = section.dataset.endpoint;
    const eventEndpoint = section.dataset.eventEndpoint || '';
    const limit = Number(section.dataset.limit || 4);
    const status = section.querySelector('[data-bk-ai-status]');
    const rows = section.querySelector('[data-bk-ai-rows]');

    if (!endpoint || !rows) return;

    const signals = profileSignals();
    const cart = await getCart();

    const payload = {
      recent_views: signals.recent_views || [],
      collections_viewed: signals.collections_viewed || [],
      search_terms: signals.search_terms || [],
      cart_items: cartForAPI(cart),
      limit
    };

    try {
      const r = await fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await r.json();
      if (!data || !data.ok || !Array.isArray(data.sections)) {
        throw new Error('No personalization');
      }

      section.__bkAiAnalytics = {
        eventEndpoint,
        profile: data.profile || {},
        sessionId: signals.session_id || ''
      };

      rows.innerHTML = '';

      for (const row of data.sections) {
        await renderSection(row, rows, section);
      }

      if (!rows.children.length) throw new Error('No rows');

      status.hidden = true;
      rows.hidden = false;
    } catch (_) {
      status.innerHTML = '<span class="bk-ai-home__sparkle">✦</span><span>Discover Bowkart</span>';
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-bk-ai-add]');
    if (button) {
      const section = button.closest('[data-bk-ai-home]');
      if (section) quickAdd(button, section);
      return;
    }

    const link = event.target.closest('[data-bk-ai-product-link]');
    if (link) {
      const card = link.closest('[data-bk-ai-product]');
      const section = link.closest('[data-bk-ai-home]');
      if (section && card) sendEvent(section, 'product_click', card);
    }
  });

  function boot() {
    document.querySelectorAll('[data-bk-ai-home]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();